import { format } from "date-fns";
import { id as idLocale } from "date-fns/locale";

// table body templates that only depend on the row

export const kendaraanBodyTemplate = (row) => {
  if (row.data_trailer && row.data_trailer.nomor) return row.data_trailer.nomor;
  if (row.data_kendaraan && row.data_kendaraan.nomor_polisi)
    return row.data_kendaraan.nomor_polisi;
  return "-";
};

export const jenisBodyTemplate = (row) => {
  if (row.data_trailer && row.data_trailer.nomor) return "Trailer";
  if (row.data_kendaraan && row.data_kendaraan.jenis_mobil)
    return row.data_kendaraan.jenis_mobil;
  return "-";
};

// "Kendaraan" column (desktop list): the plate big, the jenis small under it
export const kendaraanCompactBodyTemplate = (row) => (
  <div className="flex flex-col leading-tight">
    <span className="font-bold text-slate-800">
      {kendaraanBodyTemplate(row)}
    </span>
    <span className="text-xs text-slate-500">{jenisBodyTemplate(row)}</span>
  </div>
);

// "Problem" column (desktop list): the group as a chip, the keterangan under it.
// groupText is the label_grup as text (formatLabelGrup of the variant).
export const problemBodyTemplate = (row, groupText) => (
  <div className="flex flex-col gap-1 items-start">
    {groupText && (
      <span className="rounded-md bg-indigo-50 px-2 py-0.5 text-xs font-semibold text-indigo-600">
        {groupText}
      </span>
    )}
    <span className="text-sm leading-snug text-slate-700">
      {row.keterangan || "-"}
    </span>
  </div>
);

const formatDate = (value) =>
  value ? format(new Date(value), "d MMM yyyy", { locale: idLocale }) : "-";

// whole days from the start of the problem to the finish date (or now). Never negative:
// some old problems have a finish date before their own date.
const dayCount = (row) =>
  Math.max(
    0,
    Math.floor(
      ((row.tanggal_selesai ? new Date(row.tanggal_selesai) : new Date()) -
        new Date(row.tanggal)) /
        (1000 * 60 * 60 * 24),
    ),
  );

const formatDays = (days) =>
  days === 0 ? "di hari yang sama" : `${days.toLocaleString("id-ID")} hari`;

// What state a problem is in, from its own fields (status 2 schedule, 3 solved, 4 ignore;
// a finish date also means solved). The list never says "not handled" about a solved problem.
// solved: status 3 or a finish date (a problem can be solved without a finish date)
export const isProblemSolved = (row) => !!row.tanggal_selesai || row.status === 3;

const problemState = (row) => {
  if (isProblemSolved(row))
    return {
      key: "solved",
      label: "Selesai",
      className: "bg-green-100 text-green-700",
      icon: "pi-check-circle",
    };
  if (row.status === 4)
    return {
      key: "ignored",
      label: "Diabaikan",
      className: "bg-slate-100 text-slate-500",
      icon: "pi-ban",
    };
  if (row.status === 2)
    return {
      key: "scheduled",
      label: "Dijadwalkan",
      className: "bg-sky-100 text-sky-700",
      icon: "pi-calendar",
    };
  if (row.di_tangani_id || row.di_tangani_datetime)
    return {
      key: "handled",
      label: "Sudah ditangani",
      className: "bg-amber-100 text-amber-700",
      icon: "pi-wrench",
    };
  return {
    key: "open",
    label: "Belum ditangani",
    className: "bg-red-100 text-red-600",
    icon: "pi-exclamation-circle",
  };
};

// a small label with its value under it (same look in the Tanggal and Status columns)
const Field = ({ label, children }) => (
  <div className="flex flex-col leading-snug">
    <span className="text-[0.65rem] font-bold uppercase tracking-wide text-slate-400">
      {label}
    </span>
    {children}
  </div>
);

// "Tanggal" column (desktop list): when it was reported and, depending on the state:
//   solved with a finish date: when it was solved and how long it took
//   still open: how long it has been open (older = warmer: 7+ days amber, 30+ days red)
export const tanggalDurasiBodyTemplate = (row) => {
  const state = problemState(row);
  const days = dayCount(row);

  let ageClassName = "text-slate-600";
  if (state.key === "open" || state.key === "handled") {
    if (days >= 30) ageClassName = "text-red-600 font-semibold";
    else if (days >= 7) ageClassName = "text-amber-600 font-semibold";
  }

  return (
    <div className="flex flex-col gap-2">
      <Field label="Dilaporkan">
        <span className="text-sm font-semibold text-slate-700">
          {formatDate(row.tanggal)}
        </span>
      </Field>

      {row.tanggal_selesai ? (
        <Field label="Selesai">
          <span className="text-sm font-semibold text-green-600">
            {formatDate(row.tanggal_selesai)}
          </span>
          <span className="text-xs text-slate-400">{formatDays(days)}</span>
        </Field>
      ) : (
        state.key !== "solved" && (
          <Field label="Sudah berjalan">
            <span className={`text-sm ${ageClassName}`}>
              {days === 0 ? "Hari ini" : formatDays(days)}
            </span>
          </Field>
        )
      )}
    </div>
  );
};

// "Status" column (desktop list): one chip for the state, then who handled it and the note
// (each only when there is something). The dashboard line is only for a problem that is still open,
// because the dashboard only shows problems nobody has handled.
export const statusBodyTemplate = (row) => {
  const state = problemState(row);
  const handler = row.data_di_tangani?.nama;
  const handledAt = row.di_tangani_datetime
    ? format(new Date(row.di_tangani_datetime), "d MMM yyyy, HH:mm", {
        locale: idLocale,
      })
    : "";
  const showDashboard = state.key === "open" || state.key === "scheduled";

  return (
    <div className="flex flex-col gap-2 items-start">
      <span
        className={`row-start-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold w-fit ${state.className}`}
      >
        <i className={`pi ${state.icon} !text-xs`}></i>
        {state.label}
      </span>

      {handler && (
        <Field label="Ditangani oleh">
          <span className="text-sm font-semibold text-slate-700">
            {handler}
          </span>
          {handledAt && (
            <span className="text-xs text-slate-400">{handledAt}</span>
          )}
        </Field>
      )}

      {row.note && (
        <Field label="Note">
          <span className="text-sm text-slate-600">{row.note}</span>
        </Field>
      )}

      {showDashboard && (
        <Field label="Dashboard">
          <span
            className={`text-sm ${
              row.show_dashboard ? "text-green-600" : "text-slate-400"
            }`}
          >
            {row.show_dashboard ? "Ditampilkan" : "Disembunyikan"}
          </span>
        </Field>
      )}
    </div>
  );
};

export const durasiBodyTemplate = (row) => {
  let dayCountToToday = 0;
  const toDate = row.tanggal_selesai
    ? new Date(row.tanggal_selesai)
    : new Date();
  dayCountToToday = Math.floor(
    (toDate - new Date(row.tanggal)) / (1000 * 60 * 60 * 24),
  );
  return `${dayCountToToday.toLocaleString("id-ID")} hari`;
};

// "Dashboard" column. The dashboard shows a problem when "Show Dashboard" is on
// and nobody has handled it yet (Di tangani and Di tangani Tanggal are empty).
export const dashboardBodyTemplate = (row) => {
  let badge = {
    className: "bg-green-100 text-green-700",
    icon: "pi pi-eye",
    label: "Tampil",
  };
  if (!row.show_dashboard) {
    badge = {
      className: "bg-slate-100 text-slate-500",
      icon: "pi pi-eye-slash",
      label: "Disembunyikan",
    };
  } else if (row.di_tangani_id || row.di_tangani_datetime) {
    badge = {
      className: "bg-amber-100 text-amber-700",
      icon: "pi pi-check",
      label: "Sudah ditangani",
    };
  }

  return (
    <span
      className={`row-start-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold w-fit ${badge.className}`}
    >
      <i className={`${badge.icon} !text-xs`}></i>
      {badge.label}
    </span>
  );
};
